import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import { Box, Card, Stack, Theme, Tooltip, Typography, useMediaQuery } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LoadingButton } from '@mui/lab';
import { PasswordSchema } from '@/interfaces/schemas/users.schema';
import TextBox from '@/components/Molecules/Textbox/Textbox.component';
import { useState } from 'react';
import useUsers from '@/services/users/users.options';
import { z } from 'zod';

const PasswordForm = ({ currentUser }: { currentUser: any }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const { executeUserAction } = useUsers();

  const methods = useForm<z.infer<typeof PasswordSchema>>({
    resolver: zodResolver(PasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const confirmPassword = methods.watch('confirmPassword');
  const handlePasswordReset = async (event: React.MouseEvent) => {
    event.preventDefault();
    setIsUpdating(true);

    //for some reason the form validation is not working, so we are doing it manually
    const isValid = await methods.trigger();
    if (!isValid) {
      setIsUpdating(false);
      return;
    }

    executeUserAction(
      {
        actionType: 'resetPasswordAdmin',
        payload: {
          agentId: currentUser?.agentid,
          password: confirmPassword.trim(),
        },
      },
      {
        onSuccess: () => {
          setIsUpdating(false);
        },
        onError: () => {
          setIsUpdating(false);
        },
      },
    );
  };

  return (
    <Card sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6">Reset Password</Typography>
        <Tooltip placement="right" title="Fill out the fields below to change this user's password.">
          <InfoOutlinedIcon sx={{ color: 'primary.main' }} />
        </Tooltip>
      </Box>
      <FormProvider {...methods}>
        <Stack direction={isDesktop ? 'row' : 'column'} spacing={2}>
          <TextBox
            size="small"
            id="newPassword"
            name="newPassword"
            label="New Password"
            control={methods.control}
            sx={{ flex: 1 }}
            type="password"
          />
          <TextBox
            size="small"
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm Password"
            control={methods.control}
            sx={{ flex: 1 }}
            type="password"
          />
          <Box>
            <LoadingButton
              loading={isUpdating}
              variant="contained"
              color="primary"
              onClick={handlePasswordReset}
              type="button">
              Reset Password
            </LoadingButton>
          </Box>
        </Stack>
      </FormProvider>
    </Card>
  );
};

export default PasswordForm;
